import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    FormControl,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { serviceLocator } from './../services/service-locator';
import { notify } from './../services/ui-dialog';
import { AuthenticationService } from '../services/authentication-service/types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { UIService } from '../services/ui-service';
import logo from './../logo.png';

interface LoginProps {
    authenticationService: AuthenticationService;
    uiService: UIService;
}

interface LoginState {
    authenticationFailed: boolean;
    showPassword: boolean;
}

export class Login extends React.Component<LoginProps, LoginState>{

    constructor(props: LoginProps){
        super(props);
        this.state = { authenticationFailed: false, showPassword: false };
    }

    private handleTogglePassword = () => {
        this.setState(state => ({ showPassword: !state.showPassword }));
    }

    public render(){
        const { showPassword } = this.state;
        return (
            <Box sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: { xs: 2, sm: 4 },
                overflow: 'hidden',
                color: '#f4f6ff',
                background:
                    'radial-gradient(1200px 800px at 12% -10%, rgba(250, 135, 28, 0.20), transparent 55%),' +
                    'radial-gradient(900px 700px at 88% 5%, rgba(94, 124, 255, 0.30), transparent 55%),' +
                    'radial-gradient(900px 700px at 50% 110%, rgba(5, 35, 114, 0.65), transparent 60%),' +
                    'linear-gradient(180deg, #02093a 0%, #03124a 50%, #04154f 100%)',
                isolation: 'isolate',
            }}>
                {/* Subtle grid texture overlay — gives the void some bones. */}
                <Box aria-hidden sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    opacity: 0.5,
                    maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
                    WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
                    backgroundImage:
                        'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),' +
                        'linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
                    backgroundSize: '48px 48px',
                }} />

                {/* Soft slow-drifting glow blobs. */}
                <Box aria-hidden sx={{
                    position: 'absolute',
                    width: 480,
                    height: 480,
                    top: '-120px',
                    left: '-120px',
                    borderRadius: '50%',
                    background: 'radial-gradient(closest-side, rgba(250, 135, 28, 0.34), transparent 70%)',
                    filter: 'blur(40px)',
                    animation: 'auroraDrift 18s ease-in-out infinite',
                }} />
                <Box aria-hidden sx={{
                    position: 'absolute',
                    width: 520,
                    height: 520,
                    bottom: '-160px',
                    right: '-140px',
                    borderRadius: '50%',
                    background: 'radial-gradient(closest-side, rgba(94, 124, 255, 0.40), transparent 70%)',
                    filter: 'blur(40px)',
                    animation: 'auroraDrift 22s ease-in-out infinite reverse',
                }} />

                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 1080,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
                    gap: { xs: 3, md: 5 },
                    alignItems: 'center',
                }}>
                    {/* Editorial side — only on md+ to avoid stealing focus on mobile. */}
                    <Box sx={{ display: { xs: 'none', md: 'block' }, paddingRight: 4, animation: 'fadeUp 700ms cubic-bezier(0.16, 1, 0.3, 1) both' }}>
                        <Box
                            component="span"
                            className="eyebrow"
                            sx={{
                                color: 'rgba(255, 180, 92, 0.92)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                paddingInline: 1.25,
                                paddingBlock: 0.5,
                                borderRadius: 999,
                                border: '1px solid rgba(250, 135, 28, 0.32)',
                                background: 'rgba(250, 135, 28, 0.08)',
                            }}
                        >
                            <Box component="span" sx={{
                                display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                                background: '#FA871C',
                                boxShadow: '0 0 12px rgba(250, 135, 28, 0.7)',
                            }} />
                            WebDrvn / control room
                        </Box>
                        <Typography
                            sx={{
                                mt: 3,
                                fontFamily: '"Inter Tight", "Inter", sans-serif',
                                fontSize: { md: 56, lg: 64 },
                                fontWeight: 700,
                                letterSpacing: '-0.025em',
                                lineHeight: 1.0,
                                color: '#ffffff',
                            }}
                        >
                            Ship static<br />
                            <Box component="span" sx={{
                                background: 'linear-gradient(135deg, #ffd1a0 0%, #FA871C 60%, #ffb45c 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                            }}>
                                worth shipping.
                            </Box>
                        </Typography>
                        <Typography sx={{
                            mt: 3,
                            color: 'rgba(244, 246, 255, 0.72)',
                            fontSize: 16,
                            lineHeight: 1.6,
                            maxWidth: 460,
                        }}>
                            One control room for every Hugo build, agency, and deploy. Sign in to pick up where the team left off.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 4, mt: 5 }}>
                            {[
                                { label: 'Themes', value: 'Bootstrap 5.2 / Custom' },
                                { label: 'Pipeline', value: 'Build · QA · Deploy' },
                                { label: 'Status', value: 'All systems nominal' },
                            ].map(item => (
                                <Box key={item.label}>
                                    <Typography className="eyebrow" sx={{ color: 'rgba(244, 246, 255, 0.45)' }}>
                                        {item.label}
                                    </Typography>
                                    <Typography sx={{
                                        mt: 0.5,
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: 'rgba(244, 246, 255, 0.85)',
                                        letterSpacing: '-0.003em',
                                    }}>
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Auth card — refined glass with gradient hairline. */}
                    <Box sx={{
                        position: 'relative',
                        borderRadius: '22px',
                        padding: '1px',
                        background:
                            'linear-gradient(160deg, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0.04) 35%, rgba(250, 135, 28, 0.20) 100%)',
                        boxShadow:
                            '0 30px 80px -30px rgba(2, 10, 48, 0.7), 0 1px 0 rgba(255,255,255,0.06) inset',
                        animation: 'fadeUp 700ms cubic-bezier(0.16, 1, 0.3, 1) 80ms both',
                    }}>
                        <Box sx={{
                            borderRadius: '21px',
                            background: 'linear-gradient(180deg, rgba(8, 22, 70, 0.78) 0%, rgba(4, 16, 56, 0.86) 100%)',
                            backdropFilter: 'blur(24px)',
                            WebkitBackdropFilter: 'blur(24px)',
                            padding: { xs: 4, sm: 5 },
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '14px',
                                    flexShrink: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    padding: '8px',
                                    background: 'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.02) 100%)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    boxShadow: '0 8px 24px -8px rgba(2, 10, 48, 0.6)',
                                }} aria-hidden>
                                    <img
                                        src={logo}
                                        alt=""
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            width: 'auto',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            filter: 'brightness(0) invert(1)',
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: 11,
                                        fontWeight: 500,
                                        letterSpacing: '0.18em',
                                        textTransform: 'uppercase',
                                        color: 'rgba(244, 246, 255, 0.55)',
                                        lineHeight: 1.2,
                                    }}>
                                        WebDrvn
                                    </Typography>
                                    <Typography sx={{
                                        fontFamily: '"Inter Tight", "Inter", sans-serif',
                                        fontWeight: 700,
                                        fontSize: 22,
                                        letterSpacing: '-0.012em',
                                        color: '#ffffff',
                                        lineHeight: 1.2,
                                        mt: 0.25,
                                    }}>
                                        Sign in to continue
                                    </Typography>
                                </Box>
                            </Box>

                            <Formik
                                initialValues={{ username: '', password: '' }}
                                onSubmit={this.handleSubmit}
                                validationSchema={Yup.object().shape({
                                    username: Yup.string().required('Required'),
                                    password: Yup.string().required('Required')
                                })}
                            >
                            {props => {
                                const { values, touched, errors, isSubmitting, handleChange, handleBlur, handleSubmit } = props;
                                const usernameError = errors.username != null && touched.username != null ? errors.username : '';
                                const passwordError = errors.password != null && touched.password != null ? errors.password : '';
                                return (
                                    <form onSubmit={handleSubmit} noValidate>
                                        <TextField
                                            name="username"
                                            fullWidth
                                            autoFocus
                                            autoComplete="username"
                                            value={values.username}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            margin="normal"
                                            label="Username"
                                            error={!!usernameError}
                                            helperText={usernameError}
                                            sx={inputSx}
                                        />
                                        <TextField
                                            name="password"
                                            fullWidth
                                            autoComplete="current-password"
                                            value={values.password}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            type={showPassword ? 'text' : 'password'}
                                            margin="normal"
                                            label="Password"
                                            error={!!passwordError}
                                            helperText={passwordError}
                                            sx={inputSx}
                                            slotProps={{
                                                input: {
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                                onClick={this.handleTogglePassword}
                                                                edge="end"
                                                                size="small"
                                                                sx={{ color: 'rgba(244, 246, 255, 0.7)' }}
                                                            >
                                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                        />
                                        <FormControl margin="normal" fullWidth>
                                            <Button
                                                type="submit"
                                                disabled={isSubmitting}
                                                variant="contained"
                                                color="secondary"
                                                size="large"
                                                fullWidth
                                                startIcon={<LockOutlinedIcon />}
                                                sx={{
                                                    mt: 1,
                                                    paddingBlock: 1.4,
                                                    fontSize: 15,
                                                    background: 'linear-gradient(180deg, #FA871C 0%, #e07408 100%)',
                                                    color: '#1a0f00',
                                                    boxShadow:
                                                        '0 1px 0 rgba(255,255,255,0.25) inset, 0 14px 32px -10px rgba(250, 135, 28, 0.55)',
                                                    '&:hover': {
                                                        background: 'linear-gradient(180deg, #FA871C 0%, #FA871C 100%)',
                                                        boxShadow:
                                                            '0 1px 0 rgba(255,255,255,0.32) inset, 0 18px 36px -10px rgba(250, 135, 28, 0.7)',
                                                    },
                                                    '&:disabled': {
                                                        color: 'rgba(26, 15, 0, 0.5)',
                                                    },
                                                }}
                                            >
                                                {isSubmitting ? 'Signing in…' : 'Sign in'}
                                            </Button>
                                        </FormControl>
                                        {this.state.authenticationFailed && !isSubmitting && (
                                            <Alert
                                                severity="error"
                                                variant="outlined"
                                                sx={{
                                                    mt: 2,
                                                    borderColor: 'rgba(255, 110, 130, 0.45)',
                                                    backgroundColor: 'rgba(255, 110, 130, 0.08)',
                                                    color: '#ffd0d6',
                                                    '& .MuiAlert-icon': { color: '#ff8aa0' },
                                                }}
                                            >
                                                Username and password do not match.
                                            </Alert>
                                        )}
                                    </form>
                                );
                            }}
                        </Formik>
                            <Typography sx={{
                                mt: 3.5,
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: 11,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: 'rgba(244, 246, 255, 0.4)',
                                textAlign: 'center',
                            }}>
                                Encrypted session · Single tenant
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    private handleSubmit = async (values: any, { setSubmitting }: { setSubmitting: (value: boolean) => void }) => {
        try {
            const authenticated = await this.props.authenticationService.authenticate(values);
            if (authenticated) {
                this.setState({ authenticationFailed: false });
                this.props.uiService.forceUpdate();
            } else {
                this.setState({ authenticationFailed: true });
            }
            setSubmitting(false);
        } catch (e) {
            notify({ message: 'An error occurred while signing in.', severity: 'error' });
            setSubmitting(false);
        }
    }
}

const inputSx = {
    '& .MuiInputBase-root': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        color: '#ffffff',
        borderRadius: 2.5,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.16)',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.32)',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(250, 135, 28, 0.7)',
        borderWidth: 1.5,
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
        boxShadow: '0 0 0 4px rgba(250, 135, 28, 0.18)',
    },
    '& .MuiInputLabel-root': {
        color: 'rgba(244, 246, 255, 0.66)',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#ffb45c',
    },
    '& .MuiFormHelperText-root': {
        color: 'rgba(255, 138, 160, 0.85)',
    },
    '& input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px rgba(8, 22, 70, 0.9) inset !important',
        WebkitTextFillColor: '#ffffff !important',
        caretColor: '#ffffff',
        transition: 'background-color 5000s ease-in-out 0s',
    },
} as const;

export const LoginSmart = () => {
    return <Login
        authenticationService={serviceLocator.getAuthenticationService()}
        uiService={serviceLocator.getUIService()}
    />
}
